import React, { FC } from "react";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { ButtonColor } from "enums/Button";
import { IconType } from "enums/Icon";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import Icon from "../../../../components/Icon/Icon";
import Logo from "../../../../components/Logo/Logo";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  panelSelectData,
  panelSelectType,
} from "../../../../reduxFolder/selectors/rightPanelSelectors";
import {
  FoundPackage,
  ParcelButton,
  ParcelHeader,
  ParcelInfo,
  ParcelText,
  ParcelTitle,
  ParcelWrapper,
} from "./ParcelComponents.styles";

interface ParcelProps {
  parcel: any;
  panelData: any;
  units: string;
  foundPackageId?: number;
  foundConsolidationId?: number;
  panelType: string;
  openRightPanel: OpenRightPanel;
}

const Parcel: FC<ParcelProps> = React.memo(
  ({ parcel, units, openRightPanel, panelData, panelType, foundPackageId, foundConsolidationId }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const item = parcel.get("consolidation", Map());
    const number = item.get("sku");
    const items = item.get("items", List());
    const fullName = `${item.get("first_name")} ${item.get("last_name")}`;
    const address = `${item.get("city")}, ${item.get("postal_code")}, ${item.get(
      "country",
    )}, ${item.get("street")}${
      item.get("house") ? `, ${item.get("house")}` : ""
    }`;
    // Highlight by shipment ID (old flow) or by consolidation ID (manifest flow)
    const foundPackage =
      items.find((item: any) => item.get("shipment_id") === foundPackageId) ||
      Map();
    const foundPackageLogo = foundPackage.get("emblem_thumb_url");
    const isFoundByConsolidation =
      !!foundConsolidationId && item.get("id") === foundConsolidationId;
    const isFound = foundPackage.size > 0 || isFoundByConsolidation;

    const openParcelDetailed = () =>
      openRightPanel(
        "PARCEL_DETAILED",
        panelData
          .set("returnPreviousPanel", panelType)
          .set("number", number)
          .set("items", items)
          .set("id", parcel.getIn(["consolidation", "id"]))
          .set("sku", parcel.getIn(["consolidation", "sku"])),
      );

    return (
      <ParcelWrapper>
        <ParcelHeader>
          <ParcelTitle>
            <span>{number}</span>
            {` - ${item.get("total_package_weight", 0)} ${t(`units.${units}.primaryShort`)}`}
            {` - ${currencySymbol}${parcel.get("total_price")}`}
          </ParcelTitle>
          <div>
            {parcel.get("tracking_number") ||
              item.getIn(
                ["order", "tracking_number"],
                t("parcels.numberNotFound"),
              )}
          </div>
        </ParcelHeader>
        <ParcelInfo>
          <ParcelText>{fullName}</ParcelText>
          <ParcelText>{address}</ParcelText>
        </ParcelInfo>
        <ParcelButton
          $isSelected={isFound}
          onClick={openParcelDetailed}
          color={ButtonColor.Primary}
        >
          {isFound && (
            <FoundPackage>
              {foundPackageLogo ? (
                <Logo image={foundPackageLogo} />
              ) : (
                <Icon type={IconType.Package} />
              )}
              {foundPackage.get("shipment_tracking_number")}
            </FoundPackage>
          )}
          {`${t("parcels.moreDetails")} >>`}
        </ParcelButton>
      </ParcelWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    panelData: panelSelectData,
    panelType: panelSelectType,
  }),
  { openRightPanel },
);

export default withConnect(Parcel);
